import { useEffect, useState } from "react";
import { getMiniApps } from "api/mini-apps";
import { createTab, updateTab } from "api/tabs";
import { MiniAppType } from "types/mini-app-types";
import {
  TAB_TYPE_SUGGESTIONS,
  TAB_STATUSES,
  Tab,
  TabStatus,
} from "types/tab-types";
import { useSharedState } from "sharedStateProvider";
import { getApiErrorMessage } from "api/error";

interface NewTabSectionProps {
  editingTab: Tab | null;
  clearEditing: () => void;
  onSaved: () => void;
}

interface FormState {
  type: string;
  status: TabStatus;
  title: string;
  miniAppId: string;
}

const emptyForm: FormState = {
  type: "",
  status: "dev",
  title: "",
  miniAppId: "",
};

export default function NewTabSection({
  editingTab,
  clearEditing,
  onSaved,
}: NewTabSectionProps) {
  const { notify } = useSharedState();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [miniApps, setMiniApps] = useState<MiniAppType[]>([]);
  const [isLoadingApps, setIsLoadingApps] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const fetchMiniApps = async () => {
    setIsLoadingApps(true);
    try {
      const res = await getMiniApps(new URLSearchParams());
      setMiniApps(res.data ?? []);
    } catch (error: unknown) {
      notify(
        getApiErrorMessage(error, "Не удалось загрузить мини-приложения"),
        "danger",
        "Ошибка загрузки мини-приложений"
      );
    } finally {
      setIsLoadingApps(false);
    }
  };

  useEffect(() => {
    const modal = document.getElementById("modal-new-tab-section");
    if (!modal) return;
    const handler = () => fetchMiniApps();
    modal.addEventListener("show.bs.modal", handler);
    return () => modal.removeEventListener("show.bs.modal", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editingTab) {
      setForm({
        type: editingTab.type,
        status: editingTab.status,
        title: editingTab.title,
        miniAppId: editingTab.mini_app_id || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [editingTab]);

  const reset = () => {
    setForm(emptyForm);
    clearEditing();
  };

  const submit = async () => {
    if (!form.type.trim() || !form.title.trim()) return;

    setIsSubmitting(true);
    try {
      if (editingTab) {
        await updateTab(editingTab.id, {
          type: form.type.trim(),
          status: form.status,
          title: form.title.trim(),
          mini_app_id: form.miniAppId || null,
        });
        notify("Таб успешно обновлён", "success");
      } else {
        await createTab({
          type: form.type.trim(),
          status: form.status,
          title: form.title.trim(),
          mini_app_id: form.miniAppId || null,
        });
        notify("Таб успешно создан", "success");
      }
      onSaved();
      reset();
    } catch (error: unknown) {
      notify(
        getApiErrorMessage(error, "Не удалось сохранить таб"),
        "danger",
        "Ошибка при сохранении таба"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="modal modal-blur fade"
      id="modal-new-tab-section"
      tabIndex={-1}
      role="dialog"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {editingTab ? "Редактирование таба" : "Новый таб"}
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={reset}
            />
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-6">
                <div className="mb-3">
                  <label className="form-label required">Тип</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="services, feed, marketplace…"
                    list="tab-type-suggestions"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    disabled={!!editingTab}
                  />
                  <datalist id="tab-type-suggestions">
                    {TAB_TYPE_SUGGESTIONS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </datalist>
                </div>
              </div>
              <div className="col-6">
                <div className="mb-3">
                  <label className="form-label required">Статус</label>
                  <select
                    className="form-select"
                    value={form.status}
                    onChange={(e) =>
                      setForm({ ...form, status: e.target.value as TabStatus })
                    }
                  >
                    {TAB_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label required">Название</label>
              <input
                type="text"
                className="form-control"
                placeholder="Например: Сервисы"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Мини-приложение</label>
              <select
                className="form-select"
                value={form.miniAppId}
                onChange={(e) => setForm({ ...form, miniAppId: e.target.value })}
                disabled={isLoadingApps}
              >
                <option value="">
                  {isLoadingApps ? "Загрузка..." : "Без привязки"}
                </option>
                {miniApps.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.names.ru || app.names.en}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button
              className="btn btn-link link-secondary"
              type="reset"
              data-bs-dismiss="modal"
              onClick={reset}
            >
              Отмена
            </button>
            <button
              className="btn btn-primary ms-auto"
              type="submit"
              data-bs-dismiss="modal"
              onClick={submit}
              disabled={!form.type.trim() || !form.title.trim() || isSubmitting}
            >
              {editingTab ? "Сохранить" : "Создать таб"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
